import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  signal,
} from '@angular/core';

/** 一条落点涟漪 */
interface DemoCursorRipple {
  id: number;
  x: number;
  y: number;
}

/** 涟漪活多久（要和 .demo-cursor-ripple 的动画时长对齐），到点就从 DOM 里摘掉 */
const RIPPLE_LIFETIME_MS = 700;

/** 箭头在落点停多久再淡出 —— 得留够时间让人看清「刚才点的是这儿」 */
const CURSOR_LIFETIME_MS = 1600;

/**
 * 演示环境的「鼠标指引」。
 *
 * 演示时页面是智能体在操作，人只能看结果 —— 点一下按钮页面就跳走了，
 * 看的人不知道刚才点的是哪儿。所以这里把「按下」这一下画出来：
 * 落点一个小黑箭头 + 一圈涟漪，停一会儿再淡出。
 *
 * 🔴 两条踩过的取舍：
 *   1. **不跟随鼠标移动**。演示时人自己也会滚页面 / 划鼠标，跟随 mousemove
 *      的话箭头会追着人的手跑（上一版就是监听 mousemove，人一滚就冒箭头）。
 *      而且 mousemove 这一层**分不出**是智能体还是人在动（实测合成事件和真手
 *      事件的 sourceCapabilities 一样），所以只能整个不认。现在只看「按下去」。
 *   2. **只画智能体点的**。判据是 PointerEvent.pressure：自动化的输入管线
 *      （宿主 page_act 走 CDP）按下时是 0，真人鼠标 / 触控板是 0.5。人自己
 *      操作时浏览器本来就有小手光标，再画一个只会打架。
 *      ⚠️ 这条判据依赖宿主的输入实现。哪天 host 改了，症状是「人自己点也冒箭头」，
 *         那时把 onPress 里那行 return 去掉（改成一律画）即可。
 *
 * 🔴 只用在演示环境：真实环境里用户自己有鼠标，再画一个箭头只会碍事。
 *    所以这个组件只在 `isDemo` 为真时才渲染（见 app.component.html），
 *    它自己不重复判一次环境。
 *
 * 整层 `pointer-events: none`，纯看的，不挡任何点击。
 */
@Component({
  selector: 'app-demo-cursor',
  templateUrl: './demo-cursor.component.html',
  styleUrl: './demo-cursor.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoCursorComponent implements OnDestroy {
  readonly x = signal(0);
  readonly y = signal(0);
  readonly visible = signal(false);
  readonly pressed = signal(false);
  readonly ripples = signal<DemoCursorRipple[]>([]);

  readonly cursorTransform = computed(
    () => `translate3d(${this.x()}px, ${this.y()}px, 0)`
  );

  private rippleSeq = 0;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly rippleTimers = new Set<ReturnType<typeof setTimeout>>();
  private readonly unbind: Array<() => void> = [];

  constructor() {
    // capture 阶段挂，弹窗 / 遮罩拦不住；挂在 document 上，整页都覆盖
    this.bind('pointerdown', (event) => this.onPress(event as PointerEvent));
    this.bind('pointerup', () => this.release());
  }

  ngOnDestroy(): void {
    this.unbind.forEach((off) => off());
    this.unbind.length = 0;

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    this.rippleTimers.forEach((timer) => clearTimeout(timer));
    this.rippleTimers.clear();
  }

  private bind(type: string, handler: EventListener): void {
    document.addEventListener(type, handler, true);
    this.unbind.push(() => document.removeEventListener(type, handler, true));
  }

  private onPress(event: PointerEvent): void {
    // 真人操作（鼠标 / 触控板按下时 pressure 是 0.5）—— 他自己有小手光标，不画。
    // 严格判等：拿不到 pressure 的环境不画，宁可少画也别去碍人的事。
    if (event.pressure !== 0) {
      return;
    }

    this.x.set(event.clientX);
    this.y.set(event.clientY);
    this.visible.set(true);
    this.pressed.set(true);

    // 万一 pointerup 丢了（点完页面就跳走），别让箭头一直缩着
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
    }
    this.releaseTimer = setTimeout(() => {
      this.releaseTimer = null;
      this.pressed.set(false);
    }, 400);

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.visible.set(false);
    }, CURSOR_LIFETIME_MS);

    const id = ++this.rippleSeq;
    const ripple: DemoCursorRipple = {
      id,
      x: event.clientX,
      y: event.clientY,
    };
    this.ripples.update((list) => [...list, ripple]);

    const timer = setTimeout(() => {
      this.rippleTimers.delete(timer);
      this.ripples.update((list) => list.filter((item) => item.id !== id));
    }, RIPPLE_LIFETIME_MS);
    this.rippleTimers.add(timer);
  }

  private release(): void {
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    this.pressed.set(false);
  }
}
